import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ModeloPlantasInteractive from '../components/ModeloPlantasInteractive';
import ModeloTour3D from '../components/ModeloTour3D';
import HechoAMano, { marianaCraftImages } from '../components/HechoAMano';
import FAQSection from '../components/FAQSection';
import { Link } from 'react-router-dom';
import { trackEvent as metaTrack } from '../lib/metaTrack';

const plantasData = [
  {
    label: 'Primer Nivel',
    image: '/images/mariana-PB.webp',
    items: ['Entrada con privacidad', 'Sala amplia', 'Comedor', 'Cocina abierta', 'Área de alacena', '1/2 baño', 'Jardín interior', 'Bodega', 'Estacionamiento para 2 autos'],
  },
  {
    label: 'Segundo Nivel',
    image: '/images/mariana-PA.webp',
    items: ['Recámara principal con baño y vestidor', '3 recámaras secundarias', 'Baño completo compartido', 'Estudio / sala de TV'],
  },
  {
    label: 'Terraza',
    image: '/images/mariana-T.webp',
    items: ['Roof Garden privado', 'Espacio para asador', 'Área de lavado', 'Terraza privada'],
  },
];

const galleryImages = [
  '/images/real/sm-mar-cocina-01-lg.webp',
  '/images/real/sm-mar-roof-02-lg.webp',
  '/images/real/sm-mar-escalera-01-lg.webp',
  '/images/real/sm-mar-bano-01-lg.webp',
  '/images/real/sm-mar-recamara-01-lg.webp',
  '/images/real/sm-mar-roof-04-lg.webp',
  '/images/real/sm-mar-twilight-01-lg.webp',
  '/images/real/sm-mar-roof-01-lg.webp',
];

export default function ModeloMariana() {
  useEffect(() => {
    metaTrack('ViewContent', {
      content_name: 'Modelo Mariana',
      content_category: 'modelo',
      content_type: 'product',
    });
  }, []);

  return (
    <div className="page-wrapper">
      <Navbar />
      <Helmet>
        <html lang="es-MX" />
        <title>Modelo Mariana · Casa de 4 recámaras + roof garden · Santa Mónica Residencial Pachuca</title>
        <meta name="description" content="Modelo Mariana: 4 recámaras, 3 baños completos + 2 medios, 163 m² de construcción sobre lote 127.5 m². Roof garden con pérgola privada, cocina premium en Santa Mónica Residencial, Pachuca." />
        <meta name="keywords" content="modelo Mariana, casa Pachuca 4 recámaras, casa con pérgola Hidalgo, Santa Mónica Residencial, casa con roof garden Pachuca, INFONAVIT Pachuca premium" />
        <link rel="canonical" href="https://santamonicaresidencial.mx/modelo-mariana" />
        <meta property="og:type" content="product" />
        <meta property="og:title" content="Modelo Mariana · Santa Mónica Residencial" />
        <meta property="og:description" content="4 recámaras, 3.5 baños, 163 m² · Roof garden con pérgola privada · Pachuca, Hidalgo." />
        <meta property="og:image" content="https://santamonicaresidencial.mx/images/real/sm-mar-hero-card-lg.webp" />
        <meta property="og:url" content="https://santamonicaresidencial.mx/modelo-mariana" />
        <meta name="twitter:title" content="Modelo Mariana · Santa Mónica Residencial" />
        <meta name="twitter:description" content="4 recámaras, 3.5 baños, 163 m² · Roof garden con pérgola privada · Pachuca, Hidalgo." />
        <meta name="twitter:image" content="https://santamonicaresidencial.mx/images/real/sm-mar-hero-card-lg.webp" />
        <script type="application/ld+json">{`{
          "@context": "https://schema.org",
          "@type": "Product",
          "name": "Casa Modelo Mariana · Santa Mónica Residencial",
          "image": "https://santamonicaresidencial.mx/images/real/sm-mar-hero-card-lg.webp",
          "description": "Casa residencial de 4 recámaras, 3 baños completos + 2 medios, 163 m² de construcción sobre lote tipo de 127.5 m². Roof garden con pérgola privada y cocina premium en Pachuca, Hidalgo.",
          "brand": {"@type":"Brand","name":"Santa Mónica Residencial"},
          "category": "Casas residenciales premium en Pachuca",
          "url": "https://santamonicaresidencial.mx/modelo-mariana"
        }`}</script>
      </Helmet>
      <main className="main-wrapper">
        {/* HERO MODELO — premium twilight backdrop */}
        <section className="hero-modelo-v2" data-testid="hero-modelo-mariana">
          <div
            className="hero-modelo-v2__bg"
            style={{ backgroundImage: "url('/images/real/sm-mar-hero-card-lg.webp')" }}
          />
          <div className="hero-modelo-v2__overlay" />
          <div className="page-padding hero-modelo-v2__content">
            <div className="container-large">
              <div className="hero-modelo-v2__inner">
                <div className="hero-modelo-v2__text">
                  <div className="text-label hero-modelo-v2__eyebrow">casas residenciales en pachuca</div>
                  <h1 className="sm-h1 hero-modelo-v2__title">
                    Modelo <span className="serif-accent">Mariana</span>
                  </h1>
                  <p className="hero-modelo-v2__lead">
                    Espacios amplios y versátiles diseñados para el bienestar de toda tu familia.
                    <strong> 163 m²</strong> de construcción sobre lote tipo de <strong>127.5 m²</strong>.
                  </p>
                  <div className="hero-modelo-v2__meta">
                    <span><span className="icon-font">bed</span>3 a 4 recámaras</span>
                    <span><span className="icon-font">shower</span>3 + 2 medios</span>
                    <span><span className="icon-font">directions_car</span>2 autos</span>
                    <span><span className="icon-font">stairs</span>3 niveles</span>
                  </div>
                  <a
                    href="#proyecto"
                    className="btn cta-special contrast-variant big hero-modelo-v2__cta"
                    onClick={(e) => { e.preventDefault(); document.querySelector('#proyecto')?.scrollIntoView({ behavior: 'smooth' }); }}
                    data-testid="hero-modelo-cta"
                  >
                    <div className="btn-cta-bg" />
                    <div className="icon-btn-container">
                      <div className="icon-btn-text">Ver distribución y planos</div>
                      <div className="icon-btn-icon"><span className="icon-font">south</span></div>
                    </div>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* DISTRIBUCIÓN — Plantas interactivas + specs strip */}
        <ModeloPlantasInteractive
          modelName="Mariana"
          construccion="163 m²"
          lote="127.5 m²"
          recamaras="3 a 4"
          banos="3 + 2 medios"
          cochera="2 autos"
          levels={plantasData}
          accent="#f6802c"
        />

        {/* RECORRIDO VIRTUAL — inline cinematográfico */}
        <ModeloTour3D
          modelName="Mariana"
          matterport="https://my.matterport.com/show/?m=HMmqeW2B4t7&play=1&qs=1&hr=0&brand=0"
          poster="/images/real/sm-mar-twilight-01-lg.webp"
          accent="#f6802c"
          specs={[
            { icon: 'bed', label: '3 a 4 recámaras', sub: 'principal con vestidor' },
            { icon: 'shower', label: '3 + 2 medios', sub: 'baños completos' },
            { icon: 'directions_car', label: '2 autos', sub: 'cochera techada' },
            { icon: 'square_foot', label: '163 m²', sub: 'construcción' },
            { icon: 'crop_landscape', label: '127.5 m²', sub: 'lote tipo' },
            { icon: 'deck', label: 'Roof garden', sub: 'pérgola privada' },
          ]}
        />

        {/* CROSS-LINK to Modelo Fernanda */}
        <section className="modelo-crosslink" data-testid="crosslink-fernanda">
          <div className="modelo-crosslink-content">
            <div className="text-label" style={{ color: 'rgba(255,255,255,0.7)' }}>modelo fernanda</div>
            <h3 className="sm-h2" style={{ color: '#fff' }}>
              ¿Prefieres una opción <span className="serif-accent" style={{ color: 'var(--brand-accent)' }}>más compacta</span>?
            </h3>
            <p style={{ color: 'rgba(247,252,255,0.85)', maxWidth: '50ch' }}>
              Conoce el modelo Fernanda: 143 m² de construcción con distribución funcional y roof garden privado.
            </p>
            <Link to="/modelo-fernanda" className="btn cta-special contrast-variant big" style={{ display: 'inline-flex', marginTop: '1rem' }}>
              <div className="btn-cta-bg" />
              <div className="icon-btn-container">
                <div className="icon-btn-text">Ver modelo Fernanda</div>
                <div className="icon-btn-icon"><span className="icon-font">east</span></div>
              </div>
            </Link>
          </div>
          <div className="modelo-crosslink-image cta-fernanda" />
        </section>

        {/* HECHO A MANO */}
        <HechoAMano images={marianaCraftImages} />

        {/* FAQ */}
        <FAQSection />

        {/* CREDITOS */}
        <section className="creditos">
          <div className="page-padding">
            <div className="container-medium">
              <div className="padding-vertical padding-medium">
                <div className="_2-grid-col v-align-center">
                  <div className="element-content">
                    <div className="text-content">
                      <div className="text-label" style={{ color: 'rgba(255,255,255,0.7)' }}>créditos hipotecarios</div>
                      <h2 className="sm-h2">Pregunta por tu crédito inmobiliario</h2>
                      <p>Te ayudamos a calificar con cualquier banco o instituto público. Tu asesor compara opciones y elige la mejor tasa para tu perfil.</p>
                    </div>
                  </div>
                  <div className="element-content">
                    <img loading="lazy" src="/images/logos-creditos-1.png" alt="Créditos" style={{ width: '100%' }} />
                    <img loading="lazy" src="/images/logos-creditos-2.png" alt="Créditos" style={{ width: '100%' }} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
